import { useLazyQuery, useMutation } from "@apollo/client";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import { ExtensionsPaths } from "@dashboard/extensions/urls";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Box, Button, Text } from "@saleor/macaw-ui-next";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import {
  RestrictionFormData,
  ShippingRestrictionModal,
} from "../components/ShippingRestrictionModal";
import {
  DELETE_SHIPPING_RESTRICTIONS_METADATA,
  SHIPPING_RESTRICTIONS_PRODUCT_LIST_QUERY,
  UPDATE_SHIPPING_RESTRICTIONS_METADATA,
} from "../queries";
import { postConfigurationUpdate } from "../services/configurationApi";

interface Restriction {
  id: string;
  title: string;
  states: string[];
  products: { id: string; name: string }[];
  productMessage: string;
  checkoutMessage: string;
  zipCodes: number[];
  isActive: boolean;
}

interface ShippingRestrictionsFormData {
  restrictions: Restriction[];
  isActive: boolean;
}

const API_BASE_URL = getAppPlatformUrl("app-configurations");

const ShippingRestrictionsView: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const [formData, setFormData] = useState<ShippingRestrictionsFormData>({
    restrictions: [],
    isActive: true,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRestriction, setEditingRestriction] = useState<Restriction | null>(null);
  const [saveButtonBarState, setSaveButtonBarState] =
    useState<ConfirmButtonTransitionState>("default");
  const [hasChanges, setHasChanges] = useState(false);

  const [updateMetadata] = useMutation(UPDATE_SHIPPING_RESTRICTIONS_METADATA);
  const [deleteMetadata] = useMutation(DELETE_SHIPPING_RESTRICTIONS_METADATA);

  // State for tracking product data loading
  const [productFetchPromiseResolve, setProductFetchPromiseResolve] = useState<
    ((value: { [key: string]: string }) => void) | null
  >(null);

  const [fetchProducts] = useLazyQuery(SHIPPING_RESTRICTIONS_PRODUCT_LIST_QUERY, {
    onCompleted: data => {
      const productNameMap: { [key: string]: string } = {};

      if (data?.products?.edges) {
        data.products.edges.forEach((edge: any) => {
          productNameMap[edge.node.id] = edge.node.name;
        });
      }

      if (productFetchPromiseResolve) {
        productFetchPromiseResolve(productNameMap);
        setProductFetchPromiseResolve(null);
      }
    },
    onError: () => {
      if (productFetchPromiseResolve) {
        productFetchPromiseResolve({});
        setProductFetchPromiseResolve(null);
      }
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  // Helper function to fetch product names by IDs
  const fetchProductNames = async (productIds: string[]): Promise<{ [key: string]: string }> => {
    if (productIds.length === 0) return {};

    try {
      return new Promise(resolve => {
        setProductFetchPromiseResolve(() => (productNameMap: { [key: string]: string }) => {
          // For any missing products, fallback to a default name
          productIds.forEach(id => {
            if (!productNameMap[id]) {
              productNameMap[id] = `Product ${id}`;
            }
          });
          resolve(productNameMap);
        });

        fetchProducts({
          variables: {
            first: 100, // Fetch more products to ensure we get all needed ones
            search: "",
            channel: "default-channel",
          },
        });
      });
    } catch (error) {
      // If fetching fails, return fallback names
      const fallbackMap: { [key: string]: string } = {};

      productIds.forEach(id => {
        fallbackMap[id] = `Product ${id}`;
      });

      return fallbackMap;
    }
  };

  // Load existing restrictions from API
  useEffect(() => {
    const loadRestrictions = async () => {
      try {
        const TENANT_URL = getApiUrl();

        if (!TENANT_URL) {
          setFormData({
            restrictions: [],
            isActive: true,
          });

          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/app/get-configuration?tenant=${encodeURIComponent(TENANT_URL)}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        if (response.ok) {
          const result = await response.json();

          // Look for shipping-restrictions (with 's' for GET)
          const shippingApp = result.find((app: any) => app.app_name === "shipping-restrictions");

          if (shippingApp && shippingApp.configurations) {
            // Collect all unique product IDs from all restrictions
            const allProductIds = new Set<string>();

            Object.values(shippingApp.configurations).forEach((config: any) => {
              if (config.products) {
                config.products.forEach((id: string) => allProductIds.add(id));
              }
            });

            // Fetch product names for all product IDs
            const productNameMap = await fetchProductNames(Array.from(allProductIds));

            const restrictionsList = Object.entries(shippingApp.configurations).map(
              ([key, config]: [string, any]) => {
                // Use the saved title, or fallback to a formatted key if no title exists
                const displayTitle =
                  config.title || key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());

                return {
                  id: key,
                  title: displayTitle,
                  states: config.states || [],
                  products: config.products
                    ? config.products.map((id: string) => ({
                        id,
                        name: productNameMap[id] || `Product ${id}`,
                      }))
                    : [],
                  productMessage: config.product_message || "",
                  checkoutMessage: config.checkout_message || "",
                  zipCodes: config.zip_codes || [],
                  isActive: config.is_active ?? true,
                };
              },
            );

            setFormData({
              restrictions: restrictionsList,
              isActive: shippingApp.is_active !== false,
            });
          } else {
            // No shipping restrictions found, set empty array
            setFormData({
              restrictions: [],
              isActive: true,
            });
          }
        } else {
          // API call failed, but still show empty state
          setFormData({ restrictions: [], isActive: true });
        }
      } catch (error) {
        // Don't show error notification for initial load, just set empty state
        setFormData({ restrictions: [], isActive: true });
        throw error;
      }
    };

    loadRestrictions();
  }, []);

  const updateProductMetadata = async (productIds: string[], restrictionData: any) => {
    for (const productId of productIds) {
      const metadataInput = [
        { key: "restricted_states", value: JSON.stringify(restrictionData.states || []) },
        { key: "restricted_zip_codes", value: JSON.stringify(restrictionData.zipCodes || []) },
        { key: "product_message", value: restrictionData.productMessage || "" },
        { key: "checkout_message", value: restrictionData.checkoutMessage || "" },
        { key: "shipping_isactive", value: String(restrictionData.isActive) },
      ];

      const result = await updateMetadata({
        variables: {
          id: productId,
          input: metadataInput,
          keysToDelete: [], // No keys to delete when adding new restrictions
        },
      });

      // Check for errors in both updateMetadata and deleteMetadata responses
      const updateErrors = result.data?.updateMetadata?.errors || [];
      const deleteErrors = result.data?.deleteMetadata?.errors || [];
      const allErrors = [...updateErrors, ...deleteErrors];

      if (allErrors.length > 0) {
        throw new Error(`GraphQL errors: ${allErrors.map((e: any) => e.message).join(", ")}`);
      }
    }
  };

  const deleteProductMetadata = async (productIds: string[]) => {
    const keysToDelete = [
      "restricted_states",
      "restricted_zip_codes",
      "product_message",
      "checkout_message",
      "shipping_isactive",
    ];

    for (const productId of productIds) {
      const result = await deleteMetadata({
        variables: {
          id: productId,
          keys: keysToDelete,
        },
      });

      const errors = result.data?.deleteMetadata?.errors || [];

      if (errors.length > 0) {
        throw new Error(`GraphQL errors: ${errors.map((e: any) => e.message).join(", ")}`);
      }
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingRestriction(null);
  };

  const handleSubmit = async () => {
    setSaveButtonBarState("loading");
    setHasChanges(false);

    try {
      const TENANT_URL = getApiUrl();

      if (!TENANT_URL) {
        throw new Error("API_URL environment variable is not configured");
      }

      // Convert restrictions to API format
      const configurations: { [key: string]: any } = {};

      formData.restrictions.forEach((restriction: Restriction) => {
        configurations[restriction.id] = {
          title: restriction.title,
          is_active: restriction.isActive,
          states: restriction.states,
          products: restriction.products.map(p => p.id),
          product_message: restriction.productMessage || "",
          checkout_message: restriction.checkoutMessage || "",
          zip_codes: restriction.zipCodes,
        };
      });

      // Save to API
      const saveResponse = await postConfigurationUpdate({
        tenant: TENANT_URL,
        app_name: "shipping-restrictions",
        configurations,
        is_active: formData.isActive,
      });

      if (!saveResponse.ok) {
        throw new Error(`Failed to save configurations: ${saveResponse.status}`);
      }

      // Update product metadata for all restrictions
      for (const restriction of formData.restrictions) {
        const productIds = restriction.products.map(p => p.id);

        if (productIds.length > 0) {
          const metadataData = {
            states: restriction.states,
            zipCodes: restriction.zipCodes,
            productMessage: restriction.productMessage || "",
            checkoutMessage: restriction.checkoutMessage || "",
            isActive: restriction.isActive,
          };

          await updateProductMetadata(productIds, metadataData);
        }
      }

      setSaveButtonBarState("success");
      notify({
        status: "success",
        text: intl.formatMessage(commonMessages.savedChanges),
      });
    } catch (error) {
      setSaveButtonBarState("error");
      notify({
        status: "error",
        text: intl.formatMessage(commonMessages.somethingWentWrong),
      });
    }
  };

  const updateFormData = (updates: Partial<ShippingRestrictionsFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const isDisabled = saveButtonBarState === "loading";
  const isSaveDisabled = !hasChanges || isDisabled;

  return (
    <DetailPageLayout gridTemplateColumns={1}>
      <TopNav
        href={ExtensionsPaths.installedExtensions}
        title="Shipping Restrictions Configuration"
      />
      <DetailPageLayout.Content>
        <Box paddingX={8} paddingY={6} paddingTop={9}>
          <Box
            as="h1"
            style={{
              fontSize: "24px",
              fontWeight: 500,
              color: "#202124",
              marginBottom: "8px",
            }}
          >
            Shipping Restrictions Configuration
          </Box>
          <Box
            as="div"
            style={{
              fontSize: "14px",
              color: "#5f6368",
              marginBottom: "24px",
              lineHeight: "1.5",
            }}
          >
            Configure shipping restrictions based on location, products, and zip codes. This helps
            manage delivery limitations for specific areas or products.
          </Box>

          <Box
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              marginBottom: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              {/* <Box style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <input 
                  type="checkbox" 
                  checked={formData.isActive}
                  onChange={(e) => {
                    updateFormData({ isActive: e.target.checked });
                  }}
                  disabled={isDisabled}
                />
                <Text>Status</Text>
                <Text fontWeight="bold">{formData.isActive ? "Active" : "Inactive"}</Text>
              </Box> */}
              <Box
                as="h1"
                style={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: "#202124",
                  marginBottom: "8px",
                }}
              >
                Add Shipping Restrictions
              </Box>
              <Button variant="primary" onClick={() => setShowAddModal(true)} disabled={isDisabled}>
                Add restriction
              </Button>
            </Box>

            <Box>
              {formData.restrictions.map(
                (restriction: Restriction, index: number, array: Restriction[]) => (
                  <Box
                    key={restriction.id}
                    style={{
                      borderBottom: index === array.length - 1 ? "none" : "1px solid #e8eaed",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "16px 0",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={3}>
                      <Text size={4} fontWeight="medium">
                        {restriction.title}
                      </Text>
                      <Box
                        style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          fontWeight: 500,
                          backgroundColor: restriction.isActive ? "#e8f5e8" : "#f5f5f5",
                          color: restriction.isActive ? "#2e7d32" : "#757575",
                        }}
                      >
                        {restriction.isActive ? "Active" : "Inactive"}
                      </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => {
                          setEditingRestriction(restriction);
                          setShowAddModal(true);
                        }}
                        disabled={isDisabled}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={async () => {
                          try {
                            // Delete metadata from all products in this restriction
                            const productIds = restriction.products.map(p => p.id);

                            if (productIds.length > 0) {
                              await deleteProductMetadata(productIds);
                            }

                            // Remove restriction from form state
                            const updatedRestrictions = formData.restrictions.filter(
                              (r: Restriction) => r.id !== restriction.id,
                            );

                            updateFormData({ restrictions: updatedRestrictions });

                            notify({
                              status: "success",
                              text: "Restriction deleted successfully",
                            });
                          } catch (error) {
                            notify({
                              status: "error",
                              text: "Failed to delete restriction metadata",
                            });
                          }
                        }}
                        disabled={isDisabled}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                ),
              )}

              {formData.restrictions.length === 0 && (
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "32px",
                    color: "#666",
                    fontStyle: "italic",
                  }}
                >
                  No restrictions configured yet. Click &quot;Add restriction&quot; to create your
                  first shipping restriction.
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DetailPageLayout.Content>

      <Savebar>
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => window.history.back()} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={handleSubmit}
          disabled={isSaveDisabled}
        />
      </Savebar>

      <ShippingRestrictionModal
        open={showAddModal}
        onClose={handleCloseModal}
        onSave={(modalFormData: RestrictionFormData) => {
          // Generate restriction ID for new restriction
          const existingRestrictionNumbers = formData.restrictions
            .map((r: Restriction) => {
              const match = r.id.match(/restriction_(\d+)$/);

              return match ? parseInt(match[1]) : 0;
            })
            .filter((num: number) => !isNaN(num));

          const nextNumber =
            existingRestrictionNumbers.length > 0 ? Math.max(...existingRestrictionNumbers) + 1 : 1;

          const restrictionId = editingRestriction
            ? editingRestriction.id
            : `restriction_${nextNumber}`;

          const newRestriction = {
            id: restrictionId,
            title: modalFormData.title,
            states: modalFormData.states.map(s => s.value),
            products: modalFormData.products.map(p => ({ id: p.value, name: p.label })),
            productMessage: modalFormData.productMessage,
            checkoutMessage: modalFormData.checkoutMessage,
            zipCodes: modalFormData.zipCodes.map(z => parseInt(z.value)).filter(z => !isNaN(z)),
            isActive: modalFormData.isActive,
          };

          if (editingRestriction) {
            // Update existing restriction
            const updatedRestrictions = formData.restrictions.map((r: Restriction) =>
              r.id === editingRestriction.id ? newRestriction : r,
            );

            updateFormData({ restrictions: updatedRestrictions });
          } else {
            // Add new restriction
            updateFormData({ restrictions: [...formData.restrictions, newRestriction] });
          }

          handleCloseModal();
        }}
        loading={false}
        editingRestriction={editingRestriction}
      />
    </DetailPageLayout>
  );
};

export default ShippingRestrictionsView;
